/**
 * Feature Flags Configuration
 *
 * Controls progressive rollout of new features with safety gates.
 * All flags default to OFF for maximum safety.
 *
 * @module featureFlags
 */

/**
 * Series as Source Path Feature
 *
 * Allows indicators to use other indicators as input sources.
 * Example: EMA of RSI, SMA of MACD signal, Bollinger Bands on Volume
 *
 * Implementation Phases:
 * 1. UI_ONLY - Show dropdown options but block selection
 * 2. VALIDATION_ONLY - Allow configuration but validate without executing
 * 3. CALCULATION - Full execution with dependency-ordered processing
 */
export const SERIES_AS_SOURCE_FEATURE = {
  // Master switch - controls entire feature
  enabled: true,

  // Progressive rollout phases
  phases: {
    UI_ONLY: true, // Enable UI (dropdowns show series)
    VALIDATION_ONLY: true, // Enable validation (allow selection, validate dependencies)
    CALCULATION: true, // PHASE 3: Execute dependency-ordered calculations
  },

  // Safety limits to prevent performance issues
  limits: {
    maxDependencyDepth: 3, // Max chain: A→B→C (prevents A→B→C→D→E)
    maxTotalIndicators: 20, // Total indicator limit per chart
    maxCircularChecks: 100, // DFS iteration limit for cycle detection
    warnDependencyDepth: 2, // Show warning at depth 2, block at depth 3
  },

  // Validation strictness
  validation: {
    blockCircularReferences: true, // Prevent A→B→C→A cycles
    blockCrossPanelSources: true, // Require source in same panel
    blockRemovalWithDependents: true, // Prevent removing if others depend on it
    showDependencyWarnings: true, // Show UI warnings for complex dependencies
  },
};

/**
 * Master switch of a series-as-source feature (this module only).
 *
 * NOTE: distinct from `isFeatureEnabled(name)` in
 * `src/data/Services/settings.jsx`, which is the runtime feature-flag matrix
 * (settings.features ↔ runtime-config.json). This one only reports the master
 * `.enabled` of SERIES_AS_SOURCE; use `isPhaseEnabled` for rollout phases.
 *
 * @param {string} featureName - Name of the feature
 * @returns {boolean} Whether the feature's master switch is on
 */
export function isSeriesFeatureEnabled(featureName) {
  switch (featureName) {
    case "SERIES_AS_SOURCE":
      return SERIES_AS_SOURCE_FEATURE.enabled;
    default:
      return false;
  }
}

/**
 * Get specific phase status for a feature
 * @param {string} featureName - Name of the feature
 * @param {string} phaseName - Name of the phase
 * @returns {boolean} Whether phase is enabled
 */
export function isPhaseEnabled(featureName, phaseName) {
  switch (featureName) {
    case "SERIES_AS_SOURCE":
      return SERIES_AS_SOURCE_FEATURE.phases[phaseName] || false;
    default:
      return false;
  }
}

/**
 * Get safety limits for a feature
 * @param {string} featureName - Name of the feature
 * @returns {Object} Limits configuration
 */
export function getFeatureLimits(featureName) {
  switch (featureName) {
    case "SERIES_AS_SOURCE":
      return SERIES_AS_SOURCE_FEATURE.limits;
    default:
      return {};
  }
}

/**
 * Get validation rules for a feature
 * @param {string} featureName - Name of the feature
 * @returns {Object} Validation configuration
 */
export function getValidationRules(featureName) {
  switch (featureName) {
    case "SERIES_AS_SOURCE":
      return SERIES_AS_SOURCE_FEATURE.validation;
    default:
      return {};
  }
}

// Export all feature flags for direct access
export const FEATURE_FLAGS = {
  SERIES_AS_SOURCE: SERIES_AS_SOURCE_FEATURE,
};
